import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useContextMenu } from 'react-contexify';
import { makeStyles, Table } from '@material-ui/core';
import { slugify } from 'utils/string-helpers';
import CheckboxButton from 'components/buttons/CheckboxButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message, ThemeButton } from 'components/shared';
import { content } from './utils';
import { Body, GroupedBody, PortfolioManagersGroupByCheckbox } from './components';
import { TableHeader } from '../common/components';
import useSorting from '../common/hooks';

const TABLE_CONTEXT_MENU_ID = 'TABLE_CONTEXT_MENU_ID';

const useStyles = makeStyles(theme => {
  const { palette, customStyles } = theme;
  return {
    toolbar: {
      padding: '0 .5rem',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: '5px',
      backgroundColor: palette.grey.light
    },
    checkboxes: {
      display: 'flex'
    },
    spacer: {
      flexGrow: '2'
    },
    resetButton: {
      marginRight: customStyles.clearSortButton.marginRight,
      fontSize: customStyles.clearSortButton.fontSize
    },
    resetLink: {
      textTransform: 'uppercase'
    }
  };
});

const PortfolioManagersTable = ({
  state,
  setTableExportData,
  setQueryParam: setGroupBy,
  queryParam,
  widget,
  forwardRef
}) => {
  const classes = useStyles();
  const [groupByStrategy, setGroupByStrategy] = useState(true);
  const { show } = useContextMenu();
  const { sortBy, sortOrder, onColumnSort, resetSorting } = useSorting();
  const { isLoading, isError, error, data } = state;
  const { overlay } = widget;
  const { widgetTitle } = overlay;
  const columns = groupByStrategy ? content.groupColumns : content.defaultColumns;

  useEffect(() => {
    if (data) {
      setTableExportData({
        title: widgetTitle && slugify(widgetTitle),
        columns,
        rows: data.rows
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !data) {
    return <LoadingSpinner />;
  }

  function handleContextMenu(e, link) {
    const baseUrl = window.location.origin;
    show(e, {
      id: TABLE_CONTEXT_MENU_ID,
      props: {
        tabHref: `${baseUrl}${link}`
      }
    });
  }

  const toggleGrouping = () => {
    setGroupByStrategy(!groupByStrategy);
  };

  const renderBody = () => {
    return groupByStrategy ? (
      <GroupedBody
        dataRows={data.rows}
        groupRows={data.totals}
        columns={columns}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleContextMenu={handleContextMenu}
      />
    ) : (
      <Body
        rows={data.rows}
        totalRow={data.totals.find(t => t.strategy === 'Total')}
        columns={columns}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleContextMenu={handleContextMenu}
      />
    );
  };

  return (
    <>
      <div className={classes.toolbar}>
        <div className={classes.checkboxes}>
          <CheckboxButton
            checked={groupByStrategy}
            onChange={toggleGrouping}
            hasTransparentBg
            title="Group By Strategy"
          />
          <PortfolioManagersGroupByCheckbox
            checked={queryParam.groupBy === 'Holding'}
            onChange={setGroupBy}
            title="Combine Share Class"
          />
        </div>

        <div className={classes.spacer} />
        <ThemeButton className={classes.resetButton} onClick={resetSorting}>
          <span className={classes.resetLink}>Clear Sorting</span>
        </ThemeButton>
      </div>
      <Table ref={forwardRef}>
        <TableHeader columns={columns} onColumnSort={onColumnSort} />
        {renderBody()}
      </Table>
    </>
  );
};

PortfolioManagersTable.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})),
      totals: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  setTableExportData: PropTypes.func,
  setQueryParam: PropTypes.func,
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectType: PropTypes.string,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  queryParam: PropTypes.shape({
    groupBy: PropTypes.string
  }),
  forwardRef: PropTypes.shape({})
};

PortfolioManagersTable.defaultProps = {
  state: {},
  setTableExportData: null,
  setQueryParam: null,
  queryParam: {},
  forwardRef: null
};

export default PortfolioManagersTable;
